// Create / update / cancel / seat / finish / table-label / guest-detail
// calls for the admin reservations console, switched between v1
// (Order-centric, /reservation/*) and v2 (Reservation-centric,
// /admin/v2/reservations/*) by VITE_APP_RESERVATION_V2_ENABLED — see
// helpers/featureFlags.js. api always serves both; this flag is the only
// thing that decides which URLs the console calls.
//
// Table availability (fetchReservableTablesData) lives in reservationSlots.js
// — it already existed as a shared util before this flag, so the v1/v2
// branch was added there instead of duplicating it here.
import { isReservationV2Enabled } from "../../helpers/featureFlags";

/**
 * Creates a reservation.
 * @param {object} api - core("pu").get() instance
 * @param {string} storeId
 * @param {object} payload - { firstName, lastName, email, contact, tableId,
 *   experienceId, partySize, reservationDate, reservationTime, notes,
 *   withPaymentLink, allow_overbooking, override_reservable_days, additionals,
 *   custom_amount?, override_table_capacity? } — the v2-only fields are
 *   stripped automatically when the flag is off.
 * @returns {Promise<{order: object, paymentLink?: string}>}
 */
export const createReservation = async (api, storeId, payload) => {
  const v2 = isReservationV2Enabled();
  const { custom_amount, override_table_capacity, ...v1Fields } = payload;

  if (v2) {
    const { data } = await api.post({
      url: `/admin/v2/reservations/stores/${storeId}`,
      data: { ...v1Fields, custom_amount, override_table_capacity },
    });
    return { order: data.order, paymentLink: data.payment_link };
  }

  const { data } = await api.post({
    url: `/reservation/${storeId}/admin-book`,
    data: v1Fields,
  });
  return { order: data };
};

/**
 * Updates a reservation's date/time/table/experience/party size/guest info.
 * @param {object} api
 * @param {string} orderId
 * @param {object} payload - same shape for both modes (order_date, order_time,
 *   newTable, newExperience, party_size, notes, first_name, last_name, email,
 *   contact_number, allow_overbooking, override_reservable_days, additionals),
 *   plus v2-only custom_amount, override_table_capacity, pay_in_person.
 * @returns {Promise<{order: object, paymentLink?: string}>}
 */
export const updateReservation = async (api, orderId, payload) => {
  const v2 = isReservationV2Enabled();
  const { custom_amount, override_table_capacity, pay_in_person, ...v1Fields } =
    payload;

  if (v2) {
    const { data } = await api.put({
      url: `/admin/v2/reservations/${orderId}`,
      data: {
        ...v1Fields,
        custom_amount,
        override_table_capacity,
        pay_in_person,
      },
    });
    return {
      order: data.reservation ?? data,
      paymentLink: data.payment_link,
    };
  }

  const { data } = await api.put({
    url: `/reservation/${orderId}`,
    data: v1Fields,
  });
  return { order: data };
};

/**
 * Cancels a reservation.
 * @param {object} api
 * @param {string} orderId
 * @param {{notes?: string}} [options]
 * @returns {Promise<{order: object}>}
 */
export const cancelReservation = async (api, orderId, { notes } = {}) => {
  if (isReservationV2Enabled()) {
    const { data } = await api.post({
      url: `/admin/v2/reservations/${orderId}/cancel`,
      data: { notes },
    });
    return { order: data.order };
  }

  const { data } = await api.put({
    url: `/reservation/${orderId}/move/cancelled`,
    data: { notes },
  });
  return { order: data.result };
};

/**
 * Seats a reservation at a table.
 * @param {object} api
 * @param {string} orderId
 * @param {{tableId?: string, partySize?: number, notes?: string}} [options]
 * @returns {Promise<{order: object}>}
 */
export const seatReservation = async (
  api,
  orderId,
  { tableId, partySize, notes } = {},
) => {
  if (isReservationV2Enabled()) {
    const { data } = await api.post({
      url: `/admin/v2/reservations/${orderId}/seat`,
      data: { table_id: tableId, party_size: partySize, notes },
    });
    return { order: data.order };
  }

  const { data } = await api.put({
    url: `/reservation/${orderId}/move/accepted`,
    data: { assigned_table: tableId, party_size: partySize, notes },
  });
  return { order: data.result };
};

/**
 * Marks a reservation as finished.
 * @param {object} api
 * @param {string} orderId
 * @param {{notes?: string}} [options]
 * @returns {Promise<{order: object}>}
 */
export const finishReservation = async (api, orderId, { notes } = {}) => {
  if (isReservationV2Enabled()) {
    const { data } = await api.post({
      url: `/admin/v2/reservations/${orderId}/finish`,
      data: { notes },
    });
    return { order: data.order };
  }

  const { data } = await api.put({
    url: `/reservation/${orderId}/move/completed`,
    data: { notes },
  });
  return { order: data.result };
};

/**
 * Sets the admin-facing preferred table label (free text — distinct from
 * the actual seating table).
 * @param {object} api
 * @param {string} orderId
 * @param {string} value
 * @returns {Promise<{order: object}>}
 */
export const setPreferredTableNumber = async (api, orderId, value) => {
  const v2 = isReservationV2Enabled();
  const url = v2
    ? `/admin/v2/reservations/${orderId}/table-number`
    : `/reservation/${orderId}/table-number`;

  const { data } = await api.put({
    url,
    data: { preferred_table_number: value },
  });
  return { order: v2 ? data.order : data };
};

/**
 * Updates a reservation's guest details.
 * @param {object} api
 * @param {string} orderId
 * @param {{first_name: string, last_name: string, email: string, contact_number?: string}} guest
 * @returns {Promise<{order: object}>}
 */
export const updateGuest = async (api, orderId, guest) => {
  const v2 = isReservationV2Enabled();
  const url = v2
    ? `/admin/v2/reservations/${orderId}/guest`
    : `/reservation/${orderId}/guest-customer`;

  const { data } = await api.put({ url, data: guest });
  return { order: v2 ? data.order : data };
};

/**
 * Custom-price form value for an existing booking, from the Order's
 * reservation_notes mirror. "" when no custom price was set.
 *
 * @param {object} [reservation_notes]
 * @returns {string}
 */
export const seedCustomAmount = (reservation_notes) =>
  reservation_notes?.custom_amount != null
    ? String(reservation_notes.custom_amount)
    : "";
